import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Sparkles, Flower2 } from "lucide-react";
import { api } from "@/lib/api";
import { groupVariants } from "@/lib/variants";
import ProductCard from "@/components/ProductCard";

const MUMKEY_HERO = "/api/uploads/products/amazon/pdf_mat01_06.jpg";

const values = [
  { icon: Heart, title: "Soft on skin", copy: "Breathable, bump-kind fabrics with zero scratch." },
  { icon: Flower2, title: "Grows with you", copy: "Adjustable fits from first trimester to feeding days." },
  { icon: Sparkles, title: "Still your style", copy: "Maternity that looks like you, not like a compromise." },
];

export default function Mumkey() {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api
      .get("/products", { params: { category: "mumkey" } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, []);

  return (
    <div data-testid="mumkey-page" className="bg-[#FAF1ED]">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 pb-20 pt-16 md:px-12 lg:px-20 lg:pt-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.img
              src="/brand/mumkey-logo.png"
              alt="Mumkey"
              data-testid="mumkey-logo"
              initial={{ opacity: 0, scale: 0.9, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="h-24 w-auto sm:h-28"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-[#B76E79]"
            >
              Mumkey · by Hunar Designs
            </motion.p>
            <motion.h1
              data-testid="mumkey-hero-heading"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-serif text-5xl font-light leading-[0.98] tracking-tighter text-[#4A2C2A] sm:text-6xl lg:text-7xl xl:text-8xl"
            >
              Bloom through every <span className="italic text-[#B76E79]">trimester.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 max-w-md text-base leading-relaxed text-[#7A5A55]"
            >
              Maternity wear dresses designed to celebrate the bump, not hide it. Soft, adjustable and unapologetically pretty — from the Mumkey studio at Hunar Designs.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10">
              <a
                href="#mumkey-collection"
                data-testid="mumkey-shop-button"
                className="inline-flex items-center gap-3 rounded-full bg-[#B76E79] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#9E5B66]"
              >
                Shop Maternity Wear
              </a>
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              className="overflow-hidden rounded-t-[999px] border-8 border-[#F3DEDA]"
            >
              <motion.img
                src={MUMKEY_HERO}
                alt="Mumkey maternity wear"
                data-testid="mumkey-hero-image"
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                className="aspect-[3/4] w-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="absolute -bottom-6 -left-4 border border-[#EDD3CE] bg-white/90 px-5 py-4 backdrop-blur-md md:-left-8"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B76E79]">The Mumkey Studio</p>
              <p className="font-serif text-lg italic text-[#4A2C2A]">Maternity, made lovely</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-y border-[#EDD3CE] bg-[#F7E9E4] px-6 py-16 md:px-12 lg:px-20">
        <div className="mx-auto grid max-w-[1200px] gap-10 sm:grid-cols-3">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`mumkey-value-${i}`}
            >
              <v.icon className="h-6 w-6 text-[#B76E79]" />
              <h3 className="mt-4 font-serif text-2xl text-[#4A2C2A]">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#7A5A55]">{v.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COLLECTION */}
      <section id="mumkey-collection" className="px-6 py-20 md:px-12 lg:px-20 lg:py-28" data-testid="mumkey-collection">
        <div className="mx-auto max-w-[1400px]">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#B76E79]">The Collection</p>
          <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-[#4A2C2A] sm:text-5xl md:text-6xl">
            The Mumkey <span className="italic">Collection</span>
          </h2>

          {products === null ? (
            <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] animate-pulse bg-[#F3DEDA]" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="mt-16 font-serif text-2xl italic text-[#7A5A55]" data-testid="mumkey-empty">
              New Mumkey pieces are being stitched — check back soon.
            </p>
          ) : (
            <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 lg:grid-cols-3" data-testid="mumkey-product-grid">
              {groupVariants(products).map(({ product: p, colorCount }, i) => (
                <ProductCard key={p.id} product={p} index={i} colorCount={colorCount} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BACK LINK */}
      <section className="px-6 pb-24 md:px-12 lg:px-20">
        <Link
          to="/shop"
          data-testid="mumkey-back-link"
          className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#7A5A55] transition-colors hover:text-[#4A2C2A]"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Continue to Hunar Designs
        </Link>
      </section>
    </div>
  );
}
